import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = ['panel', 'body', 'total', 'footer', 'backdrop', 'checkoutBtn']

  connect () {
    super.connect()
    this.isOpen = false
    this.itemCount = parseInt(this.element.dataset.cartDrawerItemCountValue || '0', 10)
    this.totalAmount = parseFloat(this.element.dataset.cartDrawerTotalValue || '0')
    this.totalsUrl = this.element.dataset.cartDrawerTotalsUrlValue || '/shopping_cart/totals.json'

    this.handleCartUpdate = this.handleCartUpdate.bind(this)
    this.handleCartOpen = this.open.bind(this)
    document.addEventListener('cartUpdated', this.handleCartUpdate)
    document.addEventListener('cart:open', this.handleCartOpen)
  }

  disconnect () {
    document.removeEventListener('cartUpdated', this.handleCartUpdate)
    document.removeEventListener('cart:open', this.handleCartOpen)
  }

  open () {
    if (this.hasPanelTarget) this.panelTarget.classList.add('open')
    if (this.hasBackdropTarget) this.backdropTarget.classList.add('open')
    document.body.style.overflow = 'hidden'
    this.isOpen = true
  }

  close () {
    if (this.hasPanelTarget) this.panelTarget.classList.remove('open')
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove('open')
    document.body.style.overflow = ''
    this.isOpen = false
  }

  handleCartUpdate (event) {
    var cart = event.detail && event.detail.cart
    if (!cart) return

    var prevCount = this.itemCount
    this.itemCount = cart.item_count || 0
    this.totalAmount = cart.total || 0

    // Auto-open drawer when a new item is added
    if (this.itemCount > prevCount) {
      this.open()
    }

    // Update all cart badges on the page
    var badges = document.querySelectorAll('.cart-drawer-nav-badge')
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = this.itemCount
      if (this.itemCount > 0) {
        badges[i].classList.remove('d-none')
      } else {
        badges[i].classList.add('d-none')
      }
    }

    // Update total
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = this.formatCurrency(this.totalAmount)
    }

    // Update footer visibility
    if (this.hasFooterTarget) {
      if (this.itemCount > 0) {
        this.footerTarget.classList.remove('d-none')
      } else {
        this.footerTarget.classList.add('d-none')
      }
    }

    // Re-render items from cart data
    if (cart.items && cart.items.length > 0) {
      this.renderItems(cart.items)
    } else if (this.itemCount > 0) {
      this.fetchAndRenderItems()
    } else {
      this.renderItems([])
    }
  }

  renderItems (items) {
    if (!this.hasBodyTarget) return
    var self = this

    if (!items || items.length === 0) {
      this.bodyTarget.innerHTML =
        '<div class="cart-drawer-empty">' +
          '<i class="fas fa-shopping-cart fa-3x text-muted mb-3"></i>' +
          '<p class="text-muted">Your cart is empty</p>' +
        '</div>'
      return
    }

    var html = ''
    items.forEach(function (item) {
      if (item.purchasable_type === 'Donation') return

      var imageHtml
      if (item.image_url) {
        imageHtml = '<img src="' + item.image_url + '" alt="' + self.escapeHtml(item.name || 'Item') + '">'
      } else {
        imageHtml = '<div class="cart-drawer-item-placeholder"><i class="fas fa-tag"></i></div>'
      }

      html +=
        '<div class="cart-drawer-item" data-cart-item-id="' + item.id + '">' +
          '<div class="cart-drawer-item-image">' + imageHtml + '</div>' +
          '<div class="cart-drawer-item-details">' +
            '<div class="cart-drawer-item-name">' + self.escapeHtml(item.name || 'Item') + '</div>' +
            '<div class="cart-drawer-item-controls">' +
              '<div class="quantity-controls">' +
                '<button class="btn-qty" type="button" data-action="click->cart-drawer#decrement" data-item-id="' + item.id + '"><i class="fas fa-minus"></i></button>' +
                '<span class="qty-display">' + item.quantity + '</span>' +
                '<button class="btn-qty" type="button" data-action="click->cart-drawer#increment" data-item-id="' + item.id + '"><i class="fas fa-plus"></i></button>' +
              '</div>' +
              '<span class="cart-drawer-item-price">' + self.formatCurrency(item.total_price) + '</span>' +
            '</div>' +
          '</div>' +
          '<button class="cart-drawer-item-remove" type="button" data-action="click->cart-drawer#removeItem" data-item-id="' + item.id + '" aria-label="Remove">' +
            '<i class="fas fa-trash-alt"></i>' +
          '</button>' +
        '</div>'
    })

    this.bodyTarget.innerHTML = html || '<div class="cart-drawer-empty"><i class="fas fa-shopping-cart fa-3x text-muted mb-3"></i><p class="text-muted">Your cart is empty</p></div>'
  }

  fetchAndRenderItems () {
    var self = this
    fetch(this.totalsUrl, {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json() })
      .then(function (data) {
        var cart = data.cart || data
        self.renderItems(cart.items || [])
      })
      .catch(function () {})
  }

  increment (event) {
    var itemId = event.currentTarget.dataset.itemId
    this.updateQuantity(itemId, 1)
  }

  decrement (event) {
    var itemId = event.currentTarget.dataset.itemId
    this.updateQuantity(itemId, -1)
  }

  updateQuantity (itemId, delta) {
    var panel = this.hasPanelTarget ? this.panelTarget : this.element
    var itemEl = panel.querySelector('[data-cart-item-id="' + itemId + '"]')
    if (!itemEl) return

    var qtyDisplay = itemEl.querySelector('.qty-display')
    var currentQty = parseInt(qtyDisplay.textContent, 10)
    var newQty = currentQty + delta

    if (newQty < 0) return

    // Optimistic update
    qtyDisplay.textContent = newQty

    var csrfToken = document.querySelector('meta[name="csrf-token"]')
    var token = csrfToken ? csrfToken.content : ''

    if (newQty === 0) {
      fetch('/shopping_cart/items/' + itemId, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json() })
        .then(function (data) {
          itemEl.remove()
          document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: data.cart || data } }))
        })
    } else {
      fetch('/shopping_cart/items/' + itemId, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ shopping_cart_item: { quantity: newQty } })
      })
        .then(function (r) { return r.json() })
        .then(function (data) {
          var price = itemEl.querySelector('.cart-drawer-item-price')
          if (price && data.line_total) {
            price.textContent = '$' + parseFloat(data.line_total).toFixed(2)
          }
          document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: data.cart || data } }))
        })
    }
  }

  removeItem (event) {
    var itemId = event.currentTarget.dataset.itemId
    var panel = this.hasPanelTarget ? this.panelTarget : this.element
    var itemEl = panel.querySelector('[data-cart-item-id="' + itemId + '"]')

    var csrfToken = document.querySelector('meta[name="csrf-token"]')
    var token = csrfToken ? csrfToken.content : ''

    if (itemEl) itemEl.style.opacity = '0.5'

    fetch('/shopping_cart/items/' + itemId, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json() })
      .then(function (data) {
        if (itemEl) itemEl.remove()
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: data.cart || data } }))
      })
  }

  formatCurrency (amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  escapeHtml (str) {
    var div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
